import React, { useState, useEffect } from 'react';
import Icon from './ui/Icon';
import './InstallPrompt.css';

/**
 * Gerencia o evento beforeinstallprompt globalmente para que tanto o banner
 * dentro do app quanto a landing page possam disparar a instalação.
 */
let deferredPrompt = null;
const listeners = new Set();

function notify() { listeners.forEach(fn => fn(deferredPrompt)); }

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

/**
 * Hook para acessar o prompt de instalação de qualquer componente.
 * Retorna { canInstall, install }.
 */
export function useInstallPrompt() {
  const [prompt, setPrompt] = useState(deferredPrompt);

  useEffect(() => {
    const handler = (p) => setPrompt(p);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      deferredPrompt = null;
      notify();
    }
    return outcome === 'accepted';
  };

  return { canInstall: !!prompt, install };
}

/**
 * Banner flutuante que aparece dentro do app (logado) sugerindo instalar.
 * Desaparece se o usuário fechar ou se já estiver instalado.
 */
export default function InstallPrompt() {
  const { canInstall, install } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('pwa-banner-dismissed') === '1');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  if (isStandalone) return null;

  // iOS não suporta beforeinstallprompt, mostra dica manual
  if (isIOS && !dismissed) {
    return (
      <div className="install-banner install-banner--ios">
        <span className="install-banner__text">
          Para instalar: toque em <strong>Compartilhar</strong> (↑) e depois em <strong>"Adicionar à Tela de Início"</strong>
        </span>
        <button className="install-banner__close" onClick={() => { setDismissed(true); sessionStorage.setItem('pwa-banner-dismissed', '1'); }} aria-label="Fechar">✕</button>
      </div>
    );
  }

  if (!canInstall || dismissed) return null;

  return (
    <div className="install-banner">
      <span className="install-banner__text" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
        <Icon nome="celular" tamanho={16} />
        Instale o AgriGest no seu celular!
      </span>
      <button className="install-banner__btn" onClick={install}>Instalar</button>
      <button className="install-banner__close" onClick={() => { setDismissed(true); sessionStorage.setItem('pwa-banner-dismissed', '1'); }} aria-label="Fechar">✕</button>
    </div>
  );
}
